import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { GraduationCap, Award } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const EducationPage = () => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const data = await api.portfolio.getAll();
        setPortfolioData(data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
        toast({
          title: 'Error',
          description: 'Failed to load portfolio data.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Failed to load data.</p>
      </div>
    );
  }

  return (
    <section className="py-20 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Education & Certifications</h1>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Education */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-blue-600" />
              Education
            </h2>
            <div className="space-y-4">
              {portfolioData.education.map((edu, index) => (
                <Card key={index} className="border-blue-100 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-blue-900">{edu.degree}</CardTitle>
                    <CardDescription className="text-gray-700">{edu.university}</CardDescription>
                    <CardDescription className="text-sm text-gray-500">{edu.period}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Award className="w-6 h-6 text-blue-600" />
              Professional Certifications
            </h2>
            <div className="space-y-4">
              {portfolioData.certifications.map((cert, index) => (
                <Card key={index} className="border-blue-100 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-base text-blue-900">{cert.name}</CardTitle>
                    <CardDescription className="text-sm text-gray-600">{cert.issuer}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EducationPage;
